/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-loop-func */
import React, { useState, useEffect, forwardRef, useRef } from "react";
import Row from "./importTable/Row";
import Loader from "../Loader";
import { useDispatch, useSelector } from "react-redux";
import { flushSync } from "react-dom";
import * as databaseActions from "../../redux/actions/databaseActions";
import { FaCalendarAlt, FaCog, FaClock, FaEnvelope, FaStore } from "react-icons/fa";
import { Modal } from "@lightspeed/flame/Modal";
import { Button } from "@lightspeed/flame/Button";
import { useI18n } from "../../utils/i18n";
import { isAcombaIntegration, isAcombaSdkIntegration } from "../../utils/d365Dimensions";
import {
  forwardAcombaJournalResponse,
  postAcombaJournalEntry,
} from "../../utils/acombaSdk";

const formatD365Dimensions = (value) => {
  const cleanedValue = String(value ?? "").replace(/^\s*-\s*/, "").trim();
  if (!cleanedValue) return "";

  try {
    let parsed = cleanedValue;
    for (let index = 0; index < 3 && typeof parsed === "string"; index += 1) {
      parsed = JSON.parse(parsed);
    }

    const dimensions = Array.isArray(parsed)
      ? parsed
      : parsed?.d365_dimensions ?? parsed?.dimensions;

    if (!Array.isArray(dimensions)) return cleanedValue;

    const formatted = dimensions
      .map((dimension) => {
        const name = String(
          dimension?.dimension_code ?? dimension?.dimension_level ?? ""
        ).trim();
        const code = String(
          dimension?.code ?? dimension?.displayName ?? dimension?.id ?? ""
        ).trim();

        return [name, code].filter(Boolean).join(": ");
      })
      .filter(Boolean);

    return formatted.length > 0 ? formatted.join(" · ") : cleanedValue;
  } catch (error) {
    return cleanedValue;
  }
};

const formatImportStatus = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /[A-Za-z]/.test(value)) return value;

  const code = Number(value);
  if (!isFinite(code)) return String(value);

  return (
    {
      0: "New",
      1: "Generated",
      2: "Imported",
      3: "Posted",
      4: "Error",
      5: "Partial",
      6: "Skipped",
    }[code] ?? `Status ${code}`
  );
};

const ImportTable = forwardRef((props, ref) => {
  const { data } = props;
  const ui = useSelector((state) => state.ui);
  const userState = useSelector((state) => state.userState);
  const database = useSelector((state) => state.database);
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [showActions, setShowActions] = useState(false);
  const [sortedData, setSortedData] = useState([]);
  const [demoData, setDemoData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [bookEntries, setBookEntries] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [acombaImportResult, setAcombaImportResult] = useState(null);
  const [qbOptions, setQbOptions] = useState([]);
  const [showImportNotificationSettings, setShowImportNotificationSettings] = useState(false);
  const [automationEmailList, setAutomationEmailList] = useState("");
  const [automationHour, setAutomationHour] = useState("0");
  const [automationDayOffset, setAutomationDayOffset] = useState("-1");
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [automationSaved, setAutomationSaved] = useState(false);
  const [automationSettingsLoading, setAutomationSettingsLoading] = useState(false);
  const [automationSettingsSaving, setAutomationSettingsSaving] = useState(false);
  const [automationSettingsError, setAutomationSettingsError] = useState(null);

  const [progress, setProgress] = useState({ processed: 0, total: 0 });

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastClickedIndexRef = useRef(null);
  const detailsRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const qbOptionsRequestRef = useRef(0);
  const actionsMenuRef = useRef(null);
  const actionsDropdownRef = useRef(null);
  const currentLocationKey = [
    database.currentCompany?.id ?? "",
    database.currentCompany?.type ?? "",
    database.currentShop?.shop_id ?? "",
    database.currentShop?.area_id ?? "",
  ].join(":");

  useEffect(() => {
    if (typeof document === "undefined") return;
    const STYLE_ID = "spin-keyframes";
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.innerHTML = `
        @keyframes spin { 
          from { transform: rotate(0deg); } 
          to { transform: rotate(360deg); } 
        }`;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (!showActions || typeof document === "undefined") return;

    const handleOutsideClick = (event) => {
      const clickedButton = actionsMenuRef.current?.contains(event.target);
      const clickedDropdown = actionsDropdownRef.current?.contains(
        event.target
      );

      if (!clickedButton && !clickedDropdown) {
        setShowActions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showActions]);
  useEffect(() => {
    if (!showImportNotificationSettings) return;

    const handleImportAutomationEscape = (event) => {
      if (event.key === "Escape") {
        setShowImportNotificationSettings(false);
      }
    };

    document.addEventListener("keydown", handleImportAutomationEscape);
    return () => document.removeEventListener("keydown", handleImportAutomationEscape);
  }, [showImportNotificationSettings]);

  useEffect(() => {
    if (sortedData.length > 0 && !selectedItem) {
      handleSelectItem(sortedData[0]);
    }
  }, [sortedData]);

  useEffect(() => {
    detailsRequestRef.current += 1;
    listRequestRef.current += 1;
    qbOptionsRequestRef.current += 1;
    setSortedData([]);
    setDemoData([]);
    setSelectedItem(null);
    setAcombaImportResult(null);
    setBookEntries([]);
    setQbOptions([]);
    setSelectedIds(new Set());
    setShowActions(false);
    setVisibleCount(50);
    setProgress({ processed: 0, total: 0 });
    setIsLoadingDetails(false);
    setIsGenerating(false);
    setIsImporting(false);
    lastClickedIndexRef.current = null;
  }, [currentLocationKey]);

  useEffect(() => {
    if (Array.isArray(data)) {
      setSortedData([...data]);
      setDemoData(data);
      if (!startDate && !endDate) {
        const today = new Date();
        const fiftyDaysAgo = new Date();
        fiftyDaysAgo.setDate(today.getDate() - 49);
        setStartDate(fiftyDaysAgo.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
      }
    } else {
      setSortedData([]);
      setDemoData([]);
    }
  }, [data, startDate, endDate]);

  useEffect(() => {
    if (!Array.isArray(data)) return;

    setSelectedItem((currentItem) => {
      if (!currentItem) return currentItem;

      const refreshedItem = data.find(
        (item) => item.journalentry_id === currentItem.journalentry_id
      );

      return refreshedItem ? { ...refreshedItem } : currentItem;
    });
  }, [data]);

  const handleSelectItem = (item) => {
    const currentId = selectedItem?.journalentry_id ?? selectedItem?.journal_entry_id;
    const nextId = item?.journalentry_id ?? item?.journal_entry_id;
    if (String(currentId ?? "") !== String(nextId ?? "")) {
      setAcombaImportResult(null);
    }
    setSelectedItem({ ...item });
    fetchEntryDetails(item);
    fetchSelectedImportState(item);
  };

  const extractImportListItem = (response, fallbackItem) => {
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.items)
      ? response.items
      : response
      ? [response]
      : [];
    const fallbackId = fallbackItem?.journalentry_id ?? fallbackItem?.journal_entry_id;

    return (
      rows.find((row) => {
        const rowId = row?.journalentry_id ?? row?.journal_entry_id;
        return String(rowId) === String(fallbackId);
      }) ||
      rows[0] ||
      null
    );
  };

  const fetchSelectedImportState = async (item) => {
    if (!item) return;
    const requestId = ++listRequestRef.current;

    try {
      const response = await dispatch(
        databaseActions.getShopImport(
          item.company_id,
          userState.user.user_id,
          item.shop_id,
          item.journalentry_id ?? item.journal_entry_id
        )
      );
      const refreshedItem = extractImportListItem(response, item);

      if (requestId !== listRequestRef.current || !refreshedItem) return;
      setSelectedItem((currentItem) => {
        const currentId = currentItem?.journalentry_id ?? currentItem?.journal_entry_id;
        const itemId = item?.journalentry_id ?? item?.journal_entry_id;
        if (String(currentId) !== String(itemId)) return currentItem;
        return { ...currentItem, ...refreshedItem };
      });
    } catch (e) {
      console.warn("Import status refresh failed for:", item?.journalentry_id, e);
    }
  };

  const fetchEntryDetails = async (item) => {
    if (!item) return;
    const requestId = ++detailsRequestRef.current;
    setIsLoadingDetails(true);

    try {
      const response = await fetch(
        "https://reportapi.beesql.com/api/admin/retailr/report",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_id: item.company_id,
            shop_id: item.shop_id,
            journalentry_id: item.journalentry_id,
            xoption: "QuickBooksIMPORT GET_DETAILS",
          }),
        }
      );

      const raw = await response.text();

      if (!raw || raw.trim() === "") {
        if (requestId !== detailsRequestRef.current) return;
        setBookEntries([]);
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        console.warn("[GET_DETAILS] Body nije valjan JSON → detalji prazni.", e);
        if (requestId !== detailsRequestRef.current) return;
        setBookEntries([]);
        return;
      }

      const prepared = Array.isArray(parsed)
        ? parsed.map((e) => ({
            ...e,
            debit: isNaN(parseFloat(e.debit))
              ? "0.00"
              : parseFloat(e.debit).toFixed(2),
            credit: isNaN(parseFloat(e.credit))
              ? "0.00"
              : parseFloat(e.credit).toFixed(2),
            changed: false,
          }))
        : [];

      if (requestId !== detailsRequestRef.current) return;
      setBookEntries(prepared);
    } catch (err) {
      console.error(
        "Greška kod dohvaćanja detalja QuickBooksIMPORT GET_DETAILS:",
        err
      );
      if (requestId !== detailsRequestRef.current) return;
      setBookEntries([]);
    } finally {
      if (requestId === detailsRequestRef.current) {
        setIsLoadingDetails(false);
      }
    }
  };

  const updateEntry = (index, field, value) => {
    const updated = [...bookEntries];
    updated[index][field] = value;
    updated[index].changed = true;
    setBookEntries(updated);
  };

  useEffect(() => {
    const fetchQbOptions = async () => {
      if (!selectedItem) return;
      const requestId = ++qbOptionsRequestRef.current;
      try {
        const response = await fetch(
          "https://reportapi.beesql.com/api/admin/retailr/report",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: selectedItem.company_id,
              shop_id: selectedItem.shop_id,
              xoption: "QuickBooksIMPORT QB_ACCOUNT_LIST",
            }),
          }
        );
        const options = await response.json();
        if (requestId !== qbOptionsRequestRef.current) return;
        setQbOptions(options);
      } catch (err) {
        console.error("QB account list error:", err);
      }
    };

    fetchQbOptions();
  }, [selectedItem]);

  const saveEntry = async (entry, index) => {
    const fixedEntry = {
      ...entry,
      debit:
        parseFloat((entry.debit ?? "").toString().replace(",", ".").trim()) || 0,
      credit:
        parseFloat((entry.credit ?? "").toString().replace(",", ".").trim()) || 0,
      xoption: "QuickBooksIMPORT UPDATE_ENTRY",
    };

    fixedEntry.debit = fixedEntry.debit.toFixed(2);
    fixedEntry.credit = fixedEntry.credit.toFixed(2);

    try {
      await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fixedEntry),
      });
      const updated = [...bookEntries];
      updated[index].changed = false;
      setBookEntries(updated);
    } catch (err) {
      console.warn("Neuspješan update", err);
    }
  };

  const addEntry = async () => {
    if (!selectedItem) return;
    const newEntry = {
      journalentry_id: selectedItem.journalentry_id,
      company_id: selectedItem.company_id,
      shop_id: selectedItem.shop_id,
      date: selectedItem.journalentry_date,
      account_id: "",
      description: "",
      debit: "0.00",
      credit: "0.00",
      comment: "",
      qb_customer_id: "",
      xoption: "QuickBooksIMPORT ADD_ENTRY",
    };

    try {
      await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newEntry),
      });
      fetchEntryDetails(selectedItem);
    } catch (err) {
      console.error("Greška kod dodavanja unosa", err);
    }
  };

  const deleteEntry = async (index) => {
    const deleted = bookEntries[index];
    try {
      await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...deleted,
          xoption: "QuickBooksIMPORT DELETE_ENTRY",
        }),
      });
      const updated = [...bookEntries];
      updated.splice(index, 1);
      setBookEntries(updated);
    } catch (err) {
      console.error("Greška kod brisanja unosa", err);
    }
  };

  // ---- Helpers for selection/bulk ----
  const keyId = (x) => x.journalentry_id;
  const isChecked = (id) => selectedIds.has(id);

  const setChecked = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    lastClickedIndexRef.current = null;
  };

  const getItemsByIds = (idsSet) => {
    if (!Array.isArray(demoData) || demoData.length === 0) return [];
    const idMap = new Set(idsSet);
    return demoData.filter((x) => idMap.has(keyId(x)));
  };

  // filteredData for UI
  const filteredData = demoData.filter((item) => {
    const itemDate = new Date(item.journalentry_date);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    return (!start || itemDate >= start) && (!end || itemDate <= end);
  });

  const selectAllFiltered = () => {
    const next = new Set(
      filteredData.slice(0, visibleCount).map((x) => keyId(x))
    );
    setSelectedIds(next);
  };

  // ---- API helpers for bulk actions ----
  const generateOne = async (item) => {
    await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company_id: item.company_id,
        shop_id: item.shop_id,
        journalentry_id: item.journalentry_id,
        journalentry_date: item.journalentry_date,
        xoption: "QuickBooksIMPORT GENERATE",
      }),
    });
  };

  const importOne = async (item) => {
    const importResponse = await fetch(
      "https://reportapi.beesql.com/api/admin/retailr/report",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_id: item.company_id,
          shop_id: item.shop_id,
          journalentry_id: item.journalentry_id,
          journalentry_date: item.journalentry_date,
          xoption: "QuickBooksIMPORT IMPORT",
        }),
      }
    );

    const responseText = await importResponse.text();
    let importPayload = responseText;

    if (responseText) {
      try {
        importPayload = JSON.parse(responseText);
      } catch (error) {
        // Forward non-JSON content as text.
      }
    }

    if (!importResponse.ok) {
      throw new Error(
        importPayload?.message || responseText || `Import request failed (${importResponse.status})`
      );
    }

    if (!acombaSdkIntegration) return importPayload;

    const acombaResponse = await postAcombaJournalEntry({
      ip: database.currentShop?.ACOMBA_SDK_IP,
      port: database.currentShop?.ACOMBA_SDK_PORT,
      payload: importPayload,
    });

    await forwardAcombaJournalResponse({
      tokenUserId: userState?.user?.user_id,
      companyId: item.company_id,
      shopId: item.shop_id,
      journalEntryId: item.journalentry_id,
      responseData: acombaResponse,
    });

    return acombaResponse;
  };

  const refreshListFor = async (item) => {
    try {
      return await dispatch(
        databaseActions.getShopImport(
          item.company_id,
          userState.user.user_id,
          item.shop_id,
          item.journalentry_id ?? item.journal_entry_id
        )
      );
    } catch (e) {
      console.warn("Refresh list failed for:", item?.journalentry_id, e);
      return [];
    }
  };

  // Single import kept as fallback when no selection (but now supports bulk)
  const handleImport = async () => {
  const hasSelection = selectedIds.size > 0;
  const items = hasSelection ? getItemsByIds(selectedIds) : (selectedItem ? [selectedItem] : []);
  if (!items || items.length === 0) return;

  // postavi total i resetiraj processed
  setProgress({ processed: 0, total: items.length });

  setAcombaImportResult(null);
  flushSync(() => setIsImporting(true));
  await new Promise(requestAnimationFrame);

  try {
    let processed = 0;
    let lastRefreshedItem = null;

    for (const it of items) {
      try {
        const importResult = await importOne(it);
        if (acombaSdkIntegration) {
          setAcombaImportResult({
            success: importResult?.success !== false,
            message: importResult?.message || "Journal entry posted successfully.",
            data: importResult,
          });
        }
        const refreshedItems = await refreshListFor(it);
        lastRefreshedItem = Array.isArray(refreshedItems)
          ? refreshedItems.find(
              (item) => item.journalentry_id === it.journalentry_id
            ) || null
          : null;
      } catch (e) {
        if (acombaSdkIntegration) {
          setAcombaImportResult({
            success: false,
            message: e?.message || "Acomba journal entry post failed.",
            data: null,
          });
        }
        console.warn("Import failed for id:", it?.journalentry_id, e);
        // i na fail brojimo kao obrađeno da progress ide naprijed
      } finally {
        processed += 1;
        setProgress((prev) => ({ ...prev, processed })); // npr. 5 / 20
      }
    }

    const last = items[items.length - 1];
    if (last) {
      fetchEntryDetails(last);
      setSelectedItem({ ...(lastRefreshedItem || last) });
    }
  } catch (err) {
    console.error("Greška kod import (bulk):", err);
  } finally {
    // osiguraj full progress na kraju
    setProgress((prev) => ({ ...prev, processed: prev.total }));
    setIsImporting(false);
  }
};


  const handleGenerate = async () => {
  const hasSelection = selectedIds.size > 0;
  const items = hasSelection ? getItemsByIds(selectedIds) : (selectedItem ? [selectedItem] : []);
  if (!items || items.length === 0) return;

  // postavi total = broj stavki, processed = 0
  setProgress({ processed: 0, total: items.length });

  flushSync(() => setIsGenerating(true));
  await new Promise(requestAnimationFrame);

  try {
    let processed = 0;

    for (const it of items) {
      try {
        await generateOne(it);
        await refreshListFor(it);
      } catch (e) {
        console.warn("Generate failed for id:", it?.journalentry_id, e);
        // čak i na fail brojimo kao obrađeno, da progres ide naprijed
      } finally {
        processed += 1;
        setProgress((prev) => ({ ...prev, processed })); // npr. 5 / 20
      }
    }

    const last = items[items.length - 1];
    if (last) {
      fetchEntryDetails(last);
      setSelectedItem({ ...last });
    }
  } catch (err) {
    console.error("Greška kod generate (bulk):", err);
  } finally {
    // pobrini se da na kraju pokažemo full
    setProgress((prev) => ({ ...prev, processed: prev.total }));
    setIsGenerating(false);
  }
};


  // --- clicking/checkboxes with range & toggle ---
  const handleRowClick = (item, rowIndex, event) => {
    const id = keyId(item);
    const isMeta = event.metaKey || event.ctrlKey;
    const isShift = event.shiftKey;

    handleSelectItem(item);

    if (!isMeta && !isShift) {
      setSelectedIds(new Set([id]));
      lastClickedIndexRef.current = rowIndex;
      return;
    }
    if (isMeta) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      lastClickedIndexRef.current = rowIndex;
      return;
    }
    if (isShift) {
      const last = lastClickedIndexRef.current;
      const list = filteredData.slice(0, visibleCount);
      const safeLast = last == null ? rowIndex : last;
      const [a, b] =
        rowIndex < safeLast ? [rowIndex, safeLast] : [safeLast, rowIndex];

      const idsInRange = list.slice(a, b + 1).map((x) => keyId(x));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsInRange.forEach((rid) => next.add(rid));
        return next;
      });
      return;
    }
  };

  const handleRowCheckbox = (item, checked, rowIndex) => {
    setChecked(keyId(item), checked);
    lastClickedIndexRef.current = rowIndex;
    handleSelectItem(item);
  };

  if (!Array.isArray(sortedData) || sortedData.length === 0) {
    return <Loader />;
  }

  const hideClassColumn =
    selectedItem?.integration_name &&
    ["SAGE"].includes(selectedItem.integration_name.toUpperCase());
  const integrationName = (
    database.currentCompany?.INTEGRATION_NAME ??
    database.currentCompany?.integration_name ??
    selectedItem?.INTEGRATION_NAME ??
    selectedItem?.integration_name ??
    ""
  )
    .toString()
    .trim()
    .toUpperCase();
  const hideD365Columns = integrationName === "D365BC";
  const hideAcombagoColumns = isAcombaIntegration(database.currentCompany, database.currentShop, selectedItem);
  const acombaSdkIntegration = isAcombaSdkIntegration(
    database.currentCompany,
    database.currentShop,
    selectedItem
  );
  const selectedImportStatus = formatImportStatus(selectedItem?.import_status);
  const showImportAutomationSettingsButton =
    !acombaSdkIntegration &&
    ["RETAIL", "RESTOK"].includes(database.currentCompany?.type);
  const isRestaurantKImportAutomation = database.currentCompany?.type === "RESTOK";
  const selectedShopId = isRestaurantKImportAutomation
    ? database.currentShop?.area_id ?? ""
    : database.currentShop?.shop_id ?? "";
  const selectedShopIdText = String(selectedShopId ?? "");
  const companyShopCandidates = [
    ...(Array.isArray(database.currentCompany?.filteredShops)
      ? database.currentCompany.filteredShops
      : []),
    ...(Array.isArray(database.currentCompany?.shops)
      ? database.currentCompany.shops
      : []),
  ];
  const selectedCompanyShop = companyShopCandidates.find((shop) => {
    const candidateShopId = shop?.shop_id ?? shop?.area_id ?? shop?.id ?? "";
    return String(candidateShopId) === selectedShopIdText;
  });
  const selectedShopName = String(
    selectedCompanyShop?.shop_name ??
      selectedCompanyShop?.area_name ??
      selectedCompanyShop?.name ??
      database.currentShop?.shop_name ??
      database.currentShop?.area_name ??
      database.currentShop?.name ??
      ""
  ).trim();
  const selectedLocationName = selectedShopName
    ? selectedShopName.replace(/\s*\([^)]*\)\s*$/, "")
    : selectedShopIdText
    ? t("Selected location")
    : t("Selected shop");
  const selectedShopLabel = isRestaurantKImportAutomation
    ? selectedLocationName
    : selectedShopIdText
    ? `${selectedLocationName} (${selectedShopIdText})`
    : selectedLocationName;
  const importAutomationSettingsTitle = selectedShopIdText
    ? `${t("Automatic Posting Settings")} (${selectedShopIdText})`
    : t("Automatic Posting Settings");
  const importAutomationButtonLabel = isRestaurantKImportAutomation
    ? t("Automatic Posting Settings")
    : importAutomationSettingsTitle;
  const AUTOMATION_SETTINGS_URL = "https://reportapi.beesql.com/api/admin/retailr/report";
  const AUTOMATION_SETTINGS_GET_XOPTION = "QuickBooksIMPORT AUTOMATIC_POSTING_SETTINGS_GET";
  const AUTOMATION_SETTINGS_SAVE_XOPTION = "QuickBooksIMPORT AUTOMATIC_POSTING_SETTINGS_SAVE";

  const normalizeImportAutomationSettings = (raw) => {
    const settings = raw?.settings ?? raw?.automation_settings ?? raw ?? {};
    const enabledValue =
      settings.enabled ??
      settings.is_enabled ??
      settings.automatic_posting_enabled ??
      false;
    const hourValue =
      settings.import_hour ??
      settings.email_send_hour ??
      settings.hour ??
      settings.import_time_local ??
      0;
    const rawDayOffsetValue =
      settings.import_day_offset ??
      settings.day_offset ??
      settings.days_back ??
      -1;
    const parsedDayOffset = Number(rawDayOffsetValue);
    const dayOffsetValue = Number.isFinite(parsedDayOffset)
      ? parsedDayOffset > 0
        ? -parsedDayOffset
        : parsedDayOffset
      : -1;

    return {
      enabled:
        enabledValue === true ||
        enabledValue === "true" ||
        enabledValue === "1" ||
        enabledValue === 1 ||
        enabledValue === "on",
      email_for_notification: String(
        settings.email_for_notification ??
          settings.import_status_notification_email ??
          settings.email_list ??
          ""
      ),
      hour: String(Number.isFinite(Number(hourValue)) ? Number(hourValue) : 0),
      day_offset: String(dayOffsetValue),
    };
  };

  const buildImportAutomationRequest = (xoption, settings) => ({
    company_id: database.currentCompany?.id ?? null,
    shop_id: selectedShopIdText || null,
    area_id: isRestaurantKImportAutomation ? selectedShopIdText || null : null,
    user_id: userState.user?.user_id ?? null,
    token_company_id: database.currentCompany?.id ?? null,
    token_shop_id: selectedShopIdText || null,
    token_area_id: isRestaurantKImportAutomation ? selectedShopIdText || null : null,
    token_user_id: userState.user?.user_id ?? null,
    company_type: database.currentCompany?.type ?? null,
    xoption,
    ...(settings ? { settings } : {}),
  });

  const fetchImportAutomationSettings = async ({ signal } = {}) => {
    const response = await fetch(AUTOMATION_SETTINGS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        buildImportAutomationRequest(AUTOMATION_SETTINGS_GET_XOPTION)
      ),
      signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Load failed (${response.status}): ${text}`);
    }

    return response.json();
  };

  const openImportNotificationSettings = async () => {
    setAutomationSaved(false);
    setAutomationSettingsError(null);
    setShowImportNotificationSettings(true);
    setAutomationSettingsLoading(true);

    try {
      const data = await fetchImportAutomationSettings();
      const savedSettings = normalizeImportAutomationSettings(data);
      setAutomationEnabled(savedSettings.enabled);
      setAutomationEmailList(savedSettings.email_for_notification);
      setAutomationHour(savedSettings.hour);
      setAutomationDayOffset(savedSettings.day_offset);
    } catch (error) {
      console.error("Automatic posting settings load failed:", error);
      setAutomationSettingsError(error.message || "Failed to load settings");
      setAutomationEnabled(false);
      setAutomationEmailList("");
      setAutomationHour("0");
      setAutomationDayOffset("-1");
    } finally {
      setAutomationSettingsLoading(false);
    }
  };


  const saveImportAutomationSettings = async () => {
    const trimmedAutomationEmailList = automationEmailList.trim();

    if (!trimmedAutomationEmailList) {
      setAutomationSaved(false);
      setAutomationSettingsError("Import status notification email is required.");
      return;
    }

    const hour = Number.isFinite(+automationHour) ? +automationHour : 0;
    const dayOffset = Number.isFinite(+automationDayOffset) ? +automationDayOffset : -1;
    const settings = {
      enabled: automationEnabled,
      automatic_posting_enabled: automationEnabled ? "on" : "off",
      email_for_notification: trimmedAutomationEmailList,
      import_status_notification_email: trimmedAutomationEmailList,
      import_hour: hour,
      import_time_local: `${String(hour).padStart(2, "0")}:00`,
      import_day_offset: dayOffset,
      days_back: Math.abs(dayOffset),
      import_date_scope: dayOffset === -1 ? "yesterday" : `${Math.abs(dayOffset)}_days_ago`,
      shop_id: selectedShopIdText || null,
      area_id: isRestaurantKImportAutomation ? selectedShopIdText || null : null,
      location_type: isRestaurantKImportAutomation ? "RESTOK" : "RETAIL",
      shop_name: selectedShopName,
      updated_at: new Date().toISOString(),
    };

    setAutomationSettingsSaving(true);
    setAutomationSettingsError(null);

    try {
      const response = await fetch(AUTOMATION_SETTINGS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          buildImportAutomationRequest(AUTOMATION_SETTINGS_SAVE_XOPTION, settings)
        ),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Save failed (${response.status}): ${text}`);
      }

      setAutomationSaved(true);

      setTimeout(() => {
        setAutomationSaved(false);
        setShowImportNotificationSettings(false);
      }, 900);
    } catch (error) {
      console.error("Automatic posting settings save failed:", error);
      setAutomationSettingsError(error.message || "Failed to save settings");
    } finally {
      setAutomationSettingsSaving(false);
    }
  };

  return (
    <div style={mainStyle}>
      {/* LEFT CARD */}
      <div style={sidebarStyle}>
        <div
          style={{
            padding: "1rem",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Akcije i filteri */}
          <div style={{ marginBottom: "1rem", position: "relative" }}>
            {/* Gornji red s jednakim gumbovima */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, max-content)",
                gap: 8,
                alignItems: "center",
                width: "fit-content",
                maxWidth: "100%",
                marginBottom: "0.3rem",
              }}
            >
              <button
                ref={actionsMenuRef}
                style={controlBtnStyle}
                onClick={() => setShowActions(!showActions)}
                title={t("Actions")}
              >
                
                <span>{t("Action")}</span>
                <span style={{ marginLeft: 4 }}>▼</span>
              </button>

              <button
                style={controlBtnStyle}
                onClick={selectAllFiltered}
                title={t("Select all visible & filtered")}
              >
                
                <span>{t("Select all")}</span>
              </button>

              <button
                style={controlBtnStyle}
                onClick={clearSelection}
                title={t("Clear selection")}
              >
                
                <span>{t("Clear")}</span>
              </button>
              {showImportAutomationSettingsButton && selectedShopId !== "" && (
                <button
                  style={automationBtnStyle}
                  onClick={openImportNotificationSettings}
                  title={importAutomationSettingsTitle}
                  aria-label={importAutomationSettingsTitle}
                >
                  <FaCog aria-hidden="true" />
                  <span style={automationBtnTextStyle}>
                    <span>{importAutomationButtonLabel}</span>
                    {isRestaurantKImportAutomation && selectedShopIdText && (
                      <span style={automationBtnSubTextStyle}>{t("Location ID")}: {selectedShopIdText}</span>
                    )}
                  </span>
                </button>
              )}
            </div>

            {/* Drugi red: Selected: N */}
            <div style={{ fontSize: 12, color: "#374151", marginBottom: 8 }}>
              {t("Selected:")} {selectedIds.size}
            </div>

            {showActions && (
              <div
                ref={actionsDropdownRef}
                style={{
                  position: "absolute",
                  top: 42,
                  left: 0,
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  zIndex: 9999,
                  minWidth: "200px",
                }}
              >
                <button
                  style={dropdownButtonStyle}
                  onClick={() => {
                    setShowActions(false);
                    setTimeout(() => handleImport(), 0);
                  }}
                  title={
                    selectedIds.size > 0
                      ? `Import ${selectedIds.size} selected`
                      : "Import current"
                  }
                >
                  📥 Import {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                </button>

                <button
                  style={dropdownButtonStyle}
                  onClick={() => {
                    setShowActions(false);
                    setTimeout(() => handleGenerate(), 0);
                  }}
                  title={
                    selectedIds.size > 0
                      ? `Generate ${selectedIds.size} selected`
                      : "Generate current"
                  }
                >
                  ⚙️ Generate {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                </button>
              </div>
            )}

            {/* Date filteri */}
            <div style={{ display: "flex", gap: "10px", marginTop: 10 }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: "13px" }}>{t("From:")}</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: "13px" }}>{t("To:")}</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Scroll zona s tablicom i Load more */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f0f3f5", textAlign: "left" }}>
                  <th style={{ padding: "8px", width: 28 }}></th>
                  <th style={{ padding: "8px", minWidth: "80px" }}>{t("Date")}</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>{t("Amount")}</th>
                  <th style={{ padding: "8px" }}>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, visibleCount).map((item, idx) => {
                  const id = keyId(item);
                  return (
                    <Row
                      key={id}
                      item={item}
                      index={idx}
                      checked={isChecked(id)}
                      onCheckboxChange={(checked) =>
                        handleRowCheckbox(item, checked, idx)
                      }
                      onRowClick={(e) => handleRowClick(item, idx, e)}
                      isActive={
                        selectedItem?.journalentry_id === item.journalentry_id
                      }
                    />
                  );
                })}
              </tbody>
            </table>

            {filteredData.length > visibleCount && (
              <div style={{ padding: "1rem", textAlign: "center" }}>
                <button
                  onClick={() => setVisibleCount(visibleCount + 50)}
                  style={{ ...buttonStyle, fontSize: "13px" }}
                >
                  ➕ Load next 50
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT CARD */}
      <div style={rightCardStyle}>
        {selectedItem && (
          <div style={{ padding: "1.5rem" }}>
            <h2
  style={{
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 10,
    paddingBottom: "10px",
    marginBottom: "1rem",
    borderBottom: "2px solid #0078d4",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column", // ⬅️ bitno da se naslovi i message poredaju u stupac
    alignItems: "flex-start",
  }}
>
<div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    width: "100%",
  }}
>
  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
    📄 Details: {selectedItem.journalentry_DocNumber}

    {(isGenerating || isImporting) && (
      <>
        <svg
          width="32"
          height="32"
          viewBox="0 0 50 50"
          aria-label="Loading..."
          title="Loading..."
          style={{
            display: "inline-block",
            verticalAlign: "middle",
            marginLeft: "8px",
          }}
        >
          <g>
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="#0078d4"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="90 150"
            />
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </g>
        </svg>

        <span style={{ fontSize: 12, color: "#0078d4" }}>
          {isGenerating
            ? `Generating… ${progress.processed} / ${progress.total}`
            : `Importing… ${progress.processed} / ${progress.total}`}
        </span>
      </>
    )}
  </div>
  <span style={{ fontSize: 14, color: "#374151", whiteSpace: "nowrap" }}>
    Internal ID: {selectedItem.journalentry_id}
  </span>
</div>


  {(selectedImportStatus || selectedItem?.import_message) && (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        fontSize: 13,
        color: "#374151",
        marginTop: "4px",
      }}
    >
      {selectedImportStatus && (
        <div>
          <strong>{t("Import status")}:</strong> {selectedImportStatus}
        </div>
      )}
      {selectedItem?.import_message && <div>{selectedItem.import_message}</div>}
    </div>
  )}
    {acombaSdkIntegration && acombaImportResult && (
    <div
      role="status"
      style={{
        marginTop: "8px",
        padding: "8px 10px",
        border: `1px solid ${acombaImportResult.success ? "#16a34a" : "#dc2626"}`,
        borderRadius: "4px",
        background: acombaImportResult.success ? "#f0fdf4" : "#fef2f2",
        color: acombaImportResult.success ? "#166534" : "#991b1b",
        fontSize: 13,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      <strong>{acombaImportResult.success ? "Acomba response:" : "Acomba error:"}</strong>{" "}
      {acombaImportResult.message}
      {acombaImportResult.data != null && typeof acombaImportResult.data === "object" && (
        <div style={{ marginTop: "6px", display: "grid", gap: "2px" }}>
          <span>Step: {acombaImportResult.data.step ?? "-"}</span>
          <span>Error code: {acombaImportResult.data.errorCode ?? 0}</span>
          <span>Reference: {acombaImportResult.data.reference ?? "-"}</span>
          <span>Transaction: {acombaImportResult.data.transactionNumber ?? 0}</span>
          <span>Lines: {acombaImportResult.data.lineCount ?? 0}</span>
          <span>Amount: {acombaImportResult.data.amount ?? 0}</span>
        </div>
      )}
      {typeof acombaImportResult.data === "string" && (
        <div style={{ marginTop: "6px" }}>{acombaImportResult.data}</div>
      )}
    </div>
  )}
  <button
    style={{ ...buttonStyle, marginTop: "8px", padding: "8px 14px" }}
    onClick={addEntry}
  >
    ➕ Add item
  </button>
</h2>


            {isLoadingDetails ? (
              <div
                style={{
                  border: "2px solid red",
                  fontStyle: "italic",
                  color: "#888",
                  padding: "1rem",
                }}
              >
                ⏳ Loading data...
              </div>
            ) : (
              <div
                style={{
                  border: "2px solid red",
                  borderRadius: "6px",
                  padding: "10px",
                  marginBottom: "1rem",
                  backgroundColor: "#fff",
                }}
              >
                <table style={tableStyle}>
                  <thead style={{ backgroundColor: "#f9fafb" }}>
                    <tr>
                      <th style={thStyle}>Account ID</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Debit</th>
                      <th style={thStyle}>Credit</th>
                      <th style={thStyle}>Comment</th>
                      {!hideD365Columns && !acombaSdkIntegration && (
                        <th style={thStyle}>Internal ID</th>
                      )}
                      {!hideClassColumn && !hideAcombagoColumns && (
                        <th style={thStyle}>
                          {hideD365Columns ? "Dimensions" : "Class/Depart/Loc"}
                        </th>
                      )}
                      {!hideD365Columns && (
                        <th style={thStyle}>{acombaSdkIntegration ? "Project" : "Customer"}</th>
                      )}
                      <th style={thStyle}>💾</th>
                      <th style={thStyle}>🗑️</th>
                    </tr>
                  </thead>

                  <tbody>
                    {bookEntries.map((entry, i) => (
                      <tr key={`row-${i}`} style={{ borderTop: "1px solid #f0f3f5" }}>
                        <td style={tdStyle}>
                          <input
                            value={entry.account_id}
                            onChange={(e) =>
                              updateEntry(i, "account_id", e.target.value)
                            }
                            style={smallInputStyle}
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            value={entry.description}
                            onChange={(e) =>
                              updateEntry(i, "description", e.target.value)
                            }
                            style={inputStyle}
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            step="0.01"
                            value={entry.debit}
                            onChange={(e) => updateEntry(i, "debit", e.target.value)}
                            onBlur={(e) => {
                              const val = parseFloat(
                                e.target.value.replace(",", ".")
                              );
                              updateEntry(
                                i,
                                "debit",
                                isNaN(val) ? "0.00" : val.toFixed(2)
                              );
                            }}
                            style={smallInputStyle}
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            step="0.01"
                            value={entry.credit}
                            onChange={(e) => updateEntry(i, "credit", e.target.value)}
                            onBlur={(e) => {
                              const val = parseFloat(
                                e.target.value.replace(",", ".")
                              );
                              updateEntry(
                                i,
                                "credit",
                                isNaN(val) ? "0.00" : val.toFixed(2)
                              );
                            }}
                            style={smallInputStyle}
                          />
                        </td>
                        <td style={tdStyle}>
                          <input
                            value={entry.posting_comment}
                            onChange={(e) =>
                              updateEntry(i, "posting_comment", e.target.value)
                            }
                            style={inputStyle}
                          />
                        </td>
                        {!hideD365Columns && !acombaSdkIntegration && (
                          <td style={tdStyle}>
                            <select
                              value={entry.qb_account_id || ""}
                              onChange={(e) => {
                                const selectedId = e.target.value;
                                const selected = qbOptions.find(
                                  (opt) => opt.account_Id.toString() === selectedId
                                );
                                updateEntry(i, "qb_account_id", selectedId);
                                updateEntry(
                                  i,
                                  "qb_account_full_name",
                                  selected?.account_name_full || ""
                                );
                              }}
                              style={{ ...inputStyle, padding: "6px" }}
                            >
                              <option value="">-- Select QB Account --</option>
                              {qbOptions.map((opt, index) => (
                                <option
                                  key={`qb-${opt.account_Id || index}`}
                                  value={opt.account_Id}
                                >
                                  {opt.account_Id} - {opt.account_name_full}
                                </option>
                              ))}
                            </select>
                          </td>
                        )}
                        {!hideClassColumn && !hideAcombagoColumns && (
                          <td style={tdStyle}>
                            {hideD365Columns ? (
                              <div
                                title={String(
                                  entry.class_department_location ?? ""
                                ).replace(/^\s*-\s*/, "")}
                                style={d365DimensionsStyle}
                              >
                                {formatD365Dimensions(
                                  entry.class_department_location
                                )}
                              </div>
                            ) : (
                              <input
                                value={entry.class_department_location}
                                onChange={(e) =>
                                  updateEntry(
                                    i,
                                    "class_department_location",
                                    e.target.value
                                  )
                                }
                                style={inputStyle}
                              />
                            )}
                          </td>
                        )}
                        {!hideD365Columns && (
                          <td style={tdStyle}>
                            <input
                              value={entry.qb_customer_id || ""}
                              onChange={(e) =>
                                updateEntry(i, "qb_customer_id", e.target.value)
                              }
                              style={inputStyle}
                            />
                          </td>
                        )}
                        <td style={{ textAlign: "center" }}>
                          {entry.changed && (
                            <button
                              onClick={() => saveEntry(entry, i)}
                              style={saveButtonStyle}
                            >
                              💾
                            </button>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            onClick={() => deleteEntry(i)}
                            style={deleteButtonStyle}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr style={{ backgroundColor: "#f9fafb", fontWeight: "bold" }}>
                      <td colSpan="3" style={{ textAlign: "right", padding: "10px" }}>
                        Total:
                      </td>
                      <td style={tdStyle}>
                        {bookEntries
                          .reduce(
                            (acc, curr) => acc + parseFloat(curr.debit || 0),
                            0
                          )
                          .toFixed(2)}
                      </td>
                      <td style={tdStyle}>
                        {bookEntries
                          .reduce(
                            (acc, curr) => acc + parseFloat(curr.credit || 0),
                            0
                          )
                          .toFixed(2)}
                      </td>
                      <td
                        colSpan={
                          4 -
                          (hideD365Columns || acombaSdkIntegration ? 1 : 0) -
                          (hideClassColumn || hideAcombagoColumns ? 1 : 0) -
                          (hideD365Columns ? 1 : 0)
                        }
                      ></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {showImportNotificationSettings && (
        <Modal
          open={showImportNotificationSettings}
          isOpen={showImportNotificationSettings}
          onClose={() => setShowImportNotificationSettings(false)}
          title={importAutomationSettingsTitle}
          ariaHideApp={false}
        >
          <div style={automationModalStyle}>
            <div style={automationHeaderStyle}>
              <div style={automationHeaderIconStyle}>
                <FaCog aria-hidden="true" />
              </div>

              <div style={automationHeaderTextStyle}>
                <div style={automationEyebrowStyle}>{t("Automatic posting")}</div>
                <div style={automationTitleStyle}>{t("Posting settings")}</div>
                <div style={automationDescriptionStyle}>
                  {t("Automatically imports transactions for the selected day at the selected time.")}
                </div>
                <div style={automationShopPillStyle}>
                  <FaStore aria-hidden="true" />
                  <span>{selectedShopLabel}</span>
                </div>
                {isRestaurantKImportAutomation && selectedShopIdText && (
                  <div style={automationAreaPillStyle}>{t("Location ID")}: {selectedShopIdText}</div>
                )}
              </div>

              <label style={automationSwitchStyle}>
                <input
                  type="checkbox"
                  checked={automationEnabled}
                  onChange={(event) => setAutomationEnabled(event.target.checked)}
                  style={automationSwitchInputStyle}
                />
                <span
                  style={{
                    ...automationSwitchTrackStyle,
                    background: automationEnabled ? "#2563eb" : "#cbd5e1",
                  }}
                >
                  <span
                    style={{
                      ...automationSwitchThumbStyle,
                      transform: automationEnabled ? "translateX(18px)" : "translateX(0)",
                    }}
                  />
                </span>
                <span style={automationSwitchTextStyle}>
                  {automationEnabled ? t("Enabled") : t("Disabled")}
                </span>
              </label>
            </div>

            {(automationSettingsLoading || automationSettingsError) && (
              <div
                style={
                  automationSettingsError
                    ? automationMessageErrorStyle
                    : automationMessageInfoStyle
                }
              >
                {automationSettingsError || t("Loading automatic posting settings...")}
              </div>
            )}

            <div style={automationSettingsGridStyle}>
              <label style={automationSettingCardStyle}>
                <span style={automationSettingIconStyle}>
                  <FaEnvelope aria-hidden="true" />
                </span>
                <span style={automationFieldContentStyle}>
                  <span style={automationLabelStyle}>{t("Import status notification email (for multiple emails use ,)")}</span>
                  <input
                    type="text"
                    required
                    aria-required="true"
                    aria-invalid={Boolean(automationSettingsError && !automationEmailList.trim())}
                    placeholder="example1@hotel.com, example2@hotel.com"
                    value={automationEmailList}
                    onChange={(event) => {
                      setAutomationEmailList(event.target.value);
                      if (automationSettingsError) {
                        setAutomationSettingsError(null);
                      }
                    }}
                    style={automationInputStyle}
                  />
                </span>
              </label>

              <label style={automationSettingCardStyle}>
                <span style={automationSettingIconStyle}>
                  <FaClock aria-hidden="true" />
                </span>
                <span style={automationFieldContentStyle}>
                  <span style={automationLabelStyle}>Import time (Localtime)</span>
                  <select
                    value={automationHour}
                    onChange={(event) => setAutomationHour(event.target.value)}
                    style={automationSelectStyle}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                </span>
              </label>

              <label style={automationSettingCardStyle}>
                <span style={automationSettingIconStyle}>
                  <FaCalendarAlt aria-hidden="true" />
                </span>
                <span style={automationFieldContentStyle}>
                  <span style={automationLabelStyle}>Import data for</span>
                  <span style={automationHelpTextStyle}>Only the report from the selected day will be imported, not the full range.</span>
                  <select
                    value={automationDayOffset}
                    onChange={(event) => setAutomationDayOffset(event.target.value)}
                    style={automationSelectStyle}
                  >
                    {Array.from({ length: 7 }, (_, i) => {
                      const daysBack = i + 1;
                      const value = `-${daysBack}`;
                      return (
                        <option key={value} value={value}>
                          -{daysBack} {daysBack === 1 ? "day" : "days"}
                        </option>
                      );
                    })}
                  </select>
                </span>
              </label>
            </div>


            <div style={automationFooterStyle}>
              <div style={automationStatusStyle}>
                {automationSaved && <span>{t("Settings saved")}</span>}
              </div>

              <Button
                size="small"
                variant="secondary"
                onClick={() => setShowImportNotificationSettings(false)}
              >
                Cancel
              </Button>

              <Button
                size="small"
                fill="true"
                onClick={saveImportAutomationSettings}
                disabled={automationSettingsLoading || automationSettingsSaving}
              >
                {automationSettingsSaving ? "Saving..." : "Save settings"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
});

// Styles
const mainStyle = {
  display: "flex",
  padding: "0rem 0",
  gap: "0rem",
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  background: "#f5f7fa",
  minHeight: "100vh",
};

const sidebarStyle = {
  flex: "0 0 300px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  border: "1px solid #e0e6ed",
  height: "80vh",
  position: "sticky",
  top: "0rem",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const rightCardStyle = {
  flex: 1,
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  border: "1px solid #e0e6ed",
  display: "flex",
  flexDirection: "column",
};

const buttonStyle = {
  backgroundColor: "#0078d4",
  color: "#fff",
  border: "none",
  padding: "10px 16px",
  borderRadius: "6px",
  fontWeight: "500",
  fontSize: "14px",
  cursor: "pointer",
};

const inputStyle = {
  width: "100%",
  padding: "4px 6px",
  borderRadius: "4px",
  border: "1px solid #dce1e7",
  fontSize: "12px",
  backgroundColor: "#fefefe",
  height: "28px",
  boxSizing: "border-box",
};

const smallInputStyle = {
  ...inputStyle,
  padding: "3px 1px",
  fontSize: "12px",
  height: "28px",
  width: "80px",
};

const thStyle = { padding: "10px" };
const tdStyle = { padding: "8px" };

const d365DimensionsStyle = {
  minWidth: "220px",
  padding: "6px 8px",
  borderRadius: "4px",
  backgroundColor: "#f3f6f9",
  color: "#374151",
  fontSize: "12px",
  lineHeight: 1.4,
  whiteSpace: "normal",
};

const saveButtonStyle = {
  background: "#0078d4",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const deleteButtonStyle = {
  background: "#e74c3c",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// NOVO: ujednačene kontrole s ikonama
const controlBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  height: 36,
  padding: "0 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  color: "#111827",
  fontSize: 14,
  cursor: "pointer",
};

const iconSpan = {
  display: "inline-flex",
  width: 16,
  height: 16,
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

const tableStyle = {
  width: "100%",
  background: "#fff",
  borderRadius: "10px",
  border: "1px solid #e0e6ed",
  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
  borderCollapse: "collapse",
  fontSize: "14px",
};

const automationModalStyle = {
  width: "min(92vw, 720px)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "2px 0 0",
};

const automationHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "44px 1fr auto",
  alignItems: "center",
  gap: 14,
  padding: "18px 20px",
  border: "1px solid #d9e2ef",
  borderRadius: 8,
  background: "#f8fbff",
};

const automationHeaderIconStyle = {
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0f5fb7",
  background: "#e8f2ff",
  border: "1px solid #c8ddf5",
  fontSize: 18,
};

const automationHeaderTextStyle = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 5,
};

const automationEyebrowStyle = {
  color: "#0f5fb7",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0,
};

const automationTitleStyle = {
  color: "#111827",
  fontSize: 20,
  fontWeight: 800,
  lineHeight: 1.15,
};

const automationDescriptionStyle = {
  color: "#475569",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.35,
};
const automationAreaPillStyle = {
  width: "fit-content",
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 9px",
  borderRadius: 6,
  color: "#0f5fb7",
  background: "#e8f2ff",
  border: "1px solid #c8ddf5",
  fontSize: 12,
  fontWeight: 800,
};
const automationShopPillStyle = {
  width: "fit-content",
  maxWidth: "100%",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "5px 9px",
  borderRadius: 6,
  color: "#334155",
  background: "#ffffff",
  border: "1px solid #d9e2ef",
  fontSize: 13,
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const automationSwitchStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 9,
  color: "#1f2937",
  fontWeight: 700,
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const automationSwitchInputStyle = {
  position: "absolute",
  opacity: 0,
  pointerEvents: "none",
};

const automationSwitchTrackStyle = {
  width: 42,
  height: 24,
  padding: 2,
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  background: "#2563eb",
  boxSizing: "border-box",
  transition: "background 120ms ease",
};

const automationSwitchThumbStyle = {
  width: 20,
  height: 20,
  borderRadius: "50%",
  background: "#ffffff",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.28)",
  transition: "transform 120ms ease",
};

const automationSwitchTextStyle = {
  minWidth: 62,
};

const automationMessageInfoStyle = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 13,
  fontWeight: 700,
};

const automationMessageErrorStyle = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#b91c1c",
  fontSize: 13,
  fontWeight: 700,
};
const automationSettingsGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
};

const automationSettingCardStyle = {
  display: "grid",
  gridTemplateColumns: "36px 1fr",
  alignItems: "start",
  gap: 10,
  padding: "14px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

const automationSettingIconStyle = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0f766e",
  background: "#ecfdf5",
  border: "1px solid #bbf7d0",
  fontSize: 15,
};

const automationFieldContentStyle = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 7,
};

const automationLabelStyle = {
  color: "#334155",
  fontSize: 13,
  fontWeight: 800,
};

const automationHelpTextStyle = {
  color: "#64748b",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.35,
};
const automationInputStyle = {
  width: "100%",
  minHeight: 38,
  padding: "9px 11px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  boxSizing: "border-box",
  fontSize: 14,
  color: "#111827",
  background: "#ffffff",
};

const automationSelectStyle = {
  width: "100%",
  minHeight: 38,
  padding: "9px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  boxSizing: "border-box",
  fontSize: 14,
  color: "#111827",
  background: "#ffffff",
};

const automationFooterStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
  alignItems: "center",
  padding: "14px 18px 16px",
  borderTop: "1px solid #e5e7eb",
};

const automationStatusStyle = {
  flex: 1,
  color: "#15803d",
  fontSize: 14,
  fontWeight: 800,
};
const automationBtnStyle = {
  ...controlBtnStyle,
  gridColumn: "1 / -1",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  justifyContent: "center",
  backgroundColor: "#2563eb",
  borderColor: "#1d4ed8",
  color: "#fff",
};

const automationBtnTextStyle = {
  minWidth: 0,
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "center",
  lineHeight: 1.2,
};

const automationBtnSubTextStyle = {
  marginTop: 2,
  fontSize: 12,
  fontWeight: 700,
  opacity: 0.9,
};

const dropdownButtonStyle = {
  width: "100%",
  padding: "10px 12px",
  background: "white",
  border: "none",
  textAlign: "left",
  fontSize: "14px",
  cursor: "pointer",
  borderBottom: "1px solid #eee",
};

export default ImportTable;
